/**
 * TAKING IT APART TO FIND OUT WHAT IS WRONG, AND WHAT HAS TO BE SAID
 * BEFORE THAT HAPPENS.
 *
 * Gap 4 of five from the 2026-09-16 compliance audit: "No teardown flow.
 * Needs disassembly cost, reassembly cost, parts destroyed, restoration
 * limits and a maximum reassembly time."
 *
 * ─── A TEARDOWN IS A FOURTH KIND OF AUTHORIZATION ──────────────────────
 *
 * 0119 shipped on 2026-09-17 and made an authorization say what it
 * authorizes: repair, storage or tow. This adds 'teardown' to that list
 * rather than inventing a parallel record, for the reason 0119 itself
 * gives: pulling an engine apart to find the fault is not repair work the
 * customer asked for, it happens before anybody knows the price of the
 * repair, and it may leave the vehicle worse than it arrived. That is the
 * same argument that separated storage and towing, and it lands in the
 * same place -- its own consent, its own figure, its own paper.
 *
 * So a teardown inherits method, authorized_by, relationship, phone_used,
 * signature, authorized_at, declined, the customer's own approve button,
 * and the unique index that stops one agreement being signed eight times.
 *
 * ─── THIS IS THE ONE PLACE IN THIS WORK THAT BLOCKS ────────────────────
 *
 * Everywhere else -- papersNeedingConsent, the part condition nag, the
 * EPA nag, the A/C nag -- the rule is report, never block, because in
 * each case an honest real-world case exists where the paperwork trails
 * the event. A tow agreed on the phone at 11pm and written up next
 * morning is not a violation.
 *
 * A teardown is different, and the difference is worth being exact about.
 * The disclosure is not a record of something that happened elsewhere.
 * The disclosure IS the thing being recorded. An authorization that does
 * not say what reassembly costs or how long it takes has disclosed
 * nothing -- it is a signature on a blank, and storing it would
 * manufacture a document that looks like informed consent and is not.
 *
 * Half of this is not worth less than all of it. It is worth nothing.
 *
 * A DECLINED teardown is exempt, in the database and here. A customer
 * saying no halfway through the explanation is real, and refusing to
 * record their no until the form is finished would be perverse.
 *
 * Pure, no imports, shared with the server -- like partcondition.ts.
 */

/** What a teardown authorization has to say for itself. */
export type Teardown = {
  /** The cost of the disassembly itself. This is the row's `amount`. */
  disassembly: number | null;
  /** What it costs to put back together if the repair is not authorized. */
  reassembly: number | null;
  /** Parts and labor to replace what the teardown destroys. 0 is real. */
  destroyed: number | null;
  /** Can it go back to the condition it arrived in? Three states. */
  restorable: boolean | null;
  /** What cannot go back, when restorable is false. */
  restoreNote: string | null;
  /** The most days reassembly will take. */
  maxDays: number | null;
};

/**
 * A database value as a number, or null. Zero survives.
 *
 * "Nothing will be destroyed" is a real and common disclosure and it is
 * the number 0. It is a different statement from "nobody said", which is
 * null, and a truthiness test would merge them. Same rule as
 * acreadings.ts and for the same reason.
 */
export function amount(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Three states, never coerced to a boolean. */
export function restorable(v: unknown): boolean | null {
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return null;
}

/** Gather a teardown off an authorization row. */
export function teardownOf(row: Record<string, unknown> | null | undefined): Teardown {
  const note = String(row?.["teardown_restore_note"] ?? "").trim();
  return {
    disassembly: amount(row?.["amount"]),
    reassembly: amount(row?.["teardown_reassembly_cost"]),
    destroyed: amount(row?.["teardown_destroyed_cost"]),
    restorable: restorable(row?.["teardown_restorable"]),
    restoreNote: note.length ? note : null,
    maxDays: amount(row?.["teardown_max_days"]),
  };
}

/**
 * What is still unsaid, in the words the person filling the form needs.
 *
 * A list, not a boolean, so the form can point at the empty boxes. Empty
 * means the disclosure is complete and the customer can be asked.
 */
export function missingDisclosures(t: Teardown): string[] {
  const out: string[] = [];
  if (t.disassembly === null || t.disassembly < 0) out.push("what the teardown itself costs");
  if (t.reassembly === null || t.reassembly < 0) out.push("what it costs to put back together");
  if (t.destroyed === null || t.destroyed < 0) {
    out.push("the cost of anything the teardown destroys (enter 0 if nothing will be)");
  }
  if (t.restorable === null) out.push("whether it can be restored to how it arrived");
  if (t.restorable === false && !t.restoreNote) out.push("what cannot be restored, and why");
  if (t.maxDays === null || !(t.maxDays >= 1 && t.maxDays <= 365)) {
    out.push("the most days reassembly will take");
  }
  return out;
}

/**
 * Whether this teardown may be recorded as agreed to.
 *
 * The database enforces the same rule. Both, on purpose: the check
 * constraint is what makes it true of the data forever, and this is what
 * makes the screen say something a person can act on instead of showing
 * them a constraint name.
 */
export function mayRecord(t: Teardown, declined: boolean): boolean {
  return declined || missingDisclosures(t).length === 0;
}

/**
 * The disclosure as the customer reads it, on the approval and on the
 * paper afterwards.
 *
 * Returns the sentences as a list rather than one blob so the desk, the
 * portal and the email can each lay them out their own way without three
 * copies of the wording drifting apart.
 *
 * `usd` is passed in because this module is pure and the money formatter
 * lives with each renderer. Passing it keeps one spelling of the
 * sentences and no import.
 */
export function disclosureLines(
  t: Teardown, usd: (n: number) => string,
): string[] {
  const out: string[] = [];
  if (t.disassembly !== null) {
    out.push(`Taking it apart to find the fault costs ${usd(t.disassembly)}.`);
  }
  if (t.reassembly !== null) {
    out.push(
      t.reassembly === 0
        ? `Putting it back together, if you decide not to go ahead with the `
          + `repair, costs nothing.`
        : `If you decide not to go ahead with the repair, putting it back `
          + `together costs ${usd(t.reassembly)}.`,
    );
  }
  if (t.destroyed !== null) {
    out.push(
      t.destroyed === 0
        ? `Nothing has to be destroyed to take it apart.`
        : `Taking it apart destroys parts that cannot be reused. Replacing `
          + `them costs ${usd(t.destroyed)}, parts and labor.`,
    );
  }
  if (t.restorable === true) {
    out.push(`It can be put back to the condition it arrived in.`);
  } else if (t.restorable === false) {
    out.push(
      `It cannot be put back to the condition it arrived in.`
      + (t.restoreNote ? ` ${t.restoreNote}` : ""),
    );
  }
  if (t.maxDays !== null) {
    out.push(
      t.maxDays === 1
        ? `Reassembly will take no more than one day.`
        : `Reassembly will take no more than ${t.maxDays} days.`,
    );
  }
  return out;
}

/**
 * The heading over those sentences. One spelling, three renderers.
 *
 * Says "before" out loud, because the whole point of the document is that
 * the customer saw these numbers while the vehicle was still in one
 * piece.
 */
export const TEARDOWN_TITLE = "Before we take it apart";

/**
 * Whether a job's authorizations already carry a teardown yes.
 *
 * Used by the job screen to stop offering the form once the customer has
 * agreed, and by the invoice to decide whether to print the disclosure.
 */
export function agreedTeardown<
  T extends { authorizes?: unknown; declined?: unknown },
>(auths: readonly T[]): T | null {
  return auths.find(
    (a) => String(a.authorizes ?? "") === "teardown" && a.declined !== true,
  ) ?? null;
}
